import React, { createContext, useContext, useEffect } from "react";
import API_URL, { CMEGetContactDetailsAPIURL, CMEContactProfileAPIURL, AuthorizationCredentials } from '../config/config';
import UserContextProvider, { userContext } from './userContext';
import {validateToken} from '../api-provider';
import PromiseContextProvider from "./promiseContext";
import { useHistory, useLocation } from "react-router-dom";
import apiProfiles from "../api-operation";
import { v4 as uuid } from 'uuid';
var axios = require('axios');

// Pages an admin uses without having proxied into anyone: the admin dashboard itself and the
// admin-only tools. Every other page is a learner page and needs ?acepid= to say who it is for.
const ADMIN_PATHS_WITHOUT_LEARNER = ['/', '/index.html', '/index.htm', '/dashboard', '/dashboard/admin', '/dashboard/mergecme', '/dashboard/managerequirements'];

// The "nobody is loaded" contact. Every reset goes through this so a half-populated
// record can never survive a proxy switch and be mistaken for the new learner.
const EMPTY_CONTACT = { contactid: '', firstname: '', middlename: '', lastname: '', jobtitle: '', professionaldesignation: '', acepid: '', isABEMCertified: false, acepMemberStatus: 0, sempaMemberStatus: 0, IsNonPhysician: false, SelfAttestationUpdatedAt: null, NPI_Number: null, Birthdate: null, PARSConsentAccepted: null, DeadlineWarningLastShown: null, city: '', state: '', country: '' };

//'Context/ContactContextProvider' is used to get the authenticated users contact details and segregate that application wide or across components
export const contactContext = createContext<IContextType>({
  contactObject: { Contact: { ...EMPTY_CONTACT } },
  updateContactInfo: () => {},
  updatePARSConsent: () => {},
  updateDeadlineWarningLastShown: () => {},
  isLoading: false,
  licensesSyncKey: 0,
});
export default function ContactContextProvider(props: any) {
    
    let userObj = useContext(userContext);
    let userData = userObj.User;
    //check user is admin or not
    let isAdmin:boolean = userData.acepMemberStatus === 2 ? true : false;

    const location = useLocation();
    const history = useHistory();

    const queryParams = new URLSearchParams(location.search);

    // Get a specific parameter value by its name
    const queryAcepId = queryParams.get('acepid');
   
    // acepid of the learner we have already kicked off a fetch for. The effect below re-runs
    // on every contactObject change, so without this the same learner is fetched repeatedly
    // on a single navigation.
    const fetchingAcepIdRef = React.useRef<string | null>(null);

    const [loadPage, setLoadPage] = React.useState(false)
    const [licensesSyncKey, setLicensesSyncKey] = React.useState(0)
    const [contactObject, setContactObject]  = React.useState<{Contact: {contactid: string, firstname: string, middlename: string, lastname: string, jobtitle: string, professionaldesignation: string, acepid: string, isABEMCertified: boolean | null, acepMemberStatus: number, sempaMemberStatus: number, IsNonPhysician: boolean, SelfAttestationUpdatedAt: string | null, NPI_Number: string | null, Birthdate: string | null, PARSConsentAccepted: boolean | null, DeadlineWarningLastShown: string | null, city: string, state: string, country: string}}>({Contact: {contactid:'', firstname :'', middlename:'', lastname:'',jobtitle:'',professionaldesignation:'',acepid:'', isABEMCertified: false, acepMemberStatus: 0, sempaMemberStatus: 0, IsNonPhysician: false, SelfAttestationUpdatedAt: null, NPI_Number: null, Birthdate: null, PARSConsentAccepted: null, DeadlineWarningLastShown: null, city: '', state: '', country: ''}});

    // Function to update the contact information when a user is clicked
    const updateContactInfo = (contactData: { contactid: any; firstname: any; middlename: any; lastname: any; jobtitle: any; professionaldesignation: any; acepid: any; isABEMCertified?: any; acepMemberStatus?: any; sempaMemberStatus?: any; IsNonPhysician?: any; SelfAttestationUpdatedAt?: any; NPI_Number?: any; Birthdate?: any; PARSConsentAccepted?: any; PARSConsentUpdatedAt?: any; DeadlineWarningLastShown?: any; city?: string; state?: string; country?: string }) => {
      // Merges onto the current contact rather than replacing it: membership fields come from
      // the ACEP profile call in the effect below, and callers that only touch one field (e.g.
      // the professional-status toggle) must not blank them out and make the learner look like
      // a non-member.
      setContactObject(prev => ({
          Contact: {
              ...prev.Contact,
              contactid: String(contactData.contactid),
              firstname: contactData.firstname,
              middlename: contactData.middlename,
              lastname: contactData.lastname,
              jobtitle: contactData.jobtitle,
              professionaldesignation: contactData.professionaldesignation,
              acepid: contactData.acepid,
              isABEMCertified: contactData.isABEMCertified ?? prev.Contact.isABEMCertified,
              acepMemberStatus: Number(contactData.acepMemberStatus ?? prev.Contact.acepMemberStatus) || 0,
              sempaMemberStatus: Number(contactData.sempaMemberStatus ?? prev.Contact.sempaMemberStatus) || 0,
              IsNonPhysician: contactData.IsNonPhysician || false,
              SelfAttestationUpdatedAt: contactData.SelfAttestationUpdatedAt || null,
              NPI_Number: contactData.NPI_Number || null,
              Birthdate: contactData.Birthdate || null,
              PARSConsentAccepted: contactData.PARSConsentAccepted ?? null,
              DeadlineWarningLastShown: contactData.DeadlineWarningLastShown ?? null,
              city: contactData.city || '',
              state: contactData.state || '',
              country: contactData.country || ''
          }
      }));
      return contactData;
  };

  const updatePARSConsent = (accepted: boolean) => {
    setContactObject(prev => ({
      Contact: {
        ...prev.Contact,
        PARSConsentAccepted: accepted
      }
    }));
  };

  const updateDeadlineWarningLastShown = (date: string) => {
    setContactObject(prev => ({
      Contact: {
        ...prev.Contact,
        DeadlineWarningLastShown: date
      }
    }));
  };
  
useEffect(() => {

  // For browser refresh: Wait for userData to be loaded if not admin
  if (!isAdmin && (!userData || !userData?.acepid)) {    
    return;
  }
  
  // Installs the auth header on axios defaults for every call the app makes, so it has to run
  // before any early return below — the admin dashboard's user search goes out on this header
  // even though no learner is loaded.
  validateToken();

  // An admin only ever views a learner they explicitly proxied into, and ?acepid= is the only
  // thing that says who that is — it survives a refresh and a hand-edited URL, unlike router
  // state. With no acepid there is no learner to load, so bounce to the admin dashboard and
  // load nothing. Falling back to the admin's own acepid here is what used to leave the admin's
  // own contact sitting in contactObject, which the next proxied page then rendered and fetched
  // CME activities for before the real learner arrived.
  if (isAdmin && (queryAcepId === null || queryAcepId === '')) {
    // Clear on the way out too, so leaving a learner drops them. Otherwise the next learner's
    // page renders one pass against the previous one's contact id and fetches their CME
    // activities before the switch lands.
    if (contactObject.Contact.acepid !== '') {
      fetchingAcepIdRef.current = null;
      setContactObject({ Contact: { ...EMPTY_CONTACT } });
      setLoadPage(false);
    }
    // A learner page with nobody selected has nothing to show, so send the admin back to pick
    // someone. The admin-only pages stay put — they simply run without a contact.
    if (!ADMIN_PATHS_WITHOUT_LEARNER.includes(location.pathname.toLowerCase())) {
      history.push("/");
    }
    return;
  }

  const acepid = isAdmin ? queryAcepId : userData?.acepid;

  // Target learner changed (proxy switch, ?acepid= edited by hand, first load): clear the
  // previous one outright so nothing downstream can render or fetch with the person we just
  // navigated away from. Consumers wait on isLoading rather than reading a stale record.
  if (acepid !== contactObject.Contact.acepid) {   
    setLoadPage(false);
    if (contactObject.Contact.acepid !== '') {
      setContactObject({ Contact: { ...EMPTY_CONTACT } });
    }
  }
  
  // Make API call if we have a valid acepid and need to load data
  // For admins: queryAcepId determines the target user
  // For regular users: userData.acepid must exist
  const shouldMakeAPICall = acepid && acepid !== null && 
    (isAdmin || (userData?.acepid && userData?.acepid !== '')) &&
    (!loadPage || contactObject.Contact.acepid !== acepid);
     
  if(shouldMakeAPICall){ 
          // Already fetching this learner — the effect just re-ran because contactObject changed.
          if (fetchingAcepIdRef.current === acepid) return;
          fetchingAcepIdRef.current = acepid;

          axios.get(API_URL+ '/cmecontacts/acepid/' + acepid)
          .then(function (response:any) 
          {
            let res:any = response.data.cmecontact
            if(res){
              const acepHeaders = { headers: { 'Authorization': AuthorizationCredentials } };

              Promise.allSettled([
                axios.get(CMEGetContactDetailsAPIURL + acepid, acepHeaders),
                axios.post(CMEContactProfileAPIURL, { contactNumber: acepid }, acepHeaders)
              ]).then(async function ([acepResult, profileResult]: any[]) {
                const acepData = acepResult.status === 'fulfilled' ? acepResult.value.data : {};

                // Build sync promises — wait for all to complete before rendering
                const syncPromises: Promise<any>[] = [];
                let profileContact: any = null;
                if (profileResult.status === 'fulfilled') {
                  const profileData = profileResult.value.data;
                   profileContact = profileData.contact?.[0];
                  const primaryAddress = profileData.address?.find((a: any) => a.primaryAddress) || profileData.address?.[0];

                  // Sync contact fields (NPI, birthday, city, state, country)
                  if (profileContact || primaryAddress) {
                    syncPromises.push(
                      axios.put(API_URL + '/cmecontacts/' + res.Id, {
                        Id: res.Id,
                        Prefix: res.Prefix,
                        FirstName: res.FirstName,
                        MiddleName: res.MiddleName,
                        LastName: res.LastName,
                        FullName: res.FullName,
                        Birthdate: profileContact?.birthday ? profileContact.birthday.split('T')[0] : res.Birthdate,
                        Email: res.Email,
                        Job_Title: res.Job_Title,
                        Professional_Designation: res.Professional_Designation,
                        Phone_Number: res.Phone_Number,
                        NPI_Number: profileContact?.npi || res.NPI_Number,
                        City: primaryAddress?.city || res.City,
                        stateOrProvince: primaryAddress?.stateOrProvince || null,
                        countryIso2: primaryAddress?.country || null,
                        CME_StateProvince_Id: res.CME_StateProvince_Id,
                        CME_Country_Id: res.CME_Country_Id,
                        Acep_ContactNumber: res.Acep_ContactNumber,
                        IsActive: res.IsActive,
                        IsNonPhysician: res.IsNonPhysician,
                        SelfAttestationUpdatedAt: res.SelfAttestationUpdatedAt,
                        PARSConsentAccepted: res.PARSConsentAccepted,
                        DeadlineWarningLastShown: res.DeadlineWarningLastShown
                      }).catch((err: any) => { console.error('syncprofile PUT /cmecontacts failed:', err?.response?.status, err?.response?.data || err?.message); })
                    );
                  }

                  // Sync US-only medical licenses from credentials array
                  const medicalLicenses = (profileData.credentials || []).filter(
                    (c: any) => c.credentialType === 'MedicalLicense' && c.country === 'US'
                  );
                  const syncedLicenseIds: string[] = [];
                  medicalLicenses.forEach((credential: any) => {
                    syncedLicenseIds.push(credential.id);
                    syncPromises.push(
                      axios.put(API_URL + '/cmemedicallicense/' + credential.id, {
                        CME_Contact_Id: res.Id,
                        License_Number: credential.credentialNumber || null,
                        LicenseDate: credential.credentialBeginDate || null,
                        ExpirationDate: credential.credentialEndDate || null,
                        stateIsoCode: credential.state || null,
                        countryIso2: credential.country || null
                      }).catch(() => {})
                    );
                  });
                  // Deactivate any licenses not in this payload (non-US or removed from profile)
                  syncPromises.push(
                    axios.post(API_URL + '/cmemedicallicense/deactivateunsynced', {
                      contactId: res.Id,
                      syncedIds: syncedLicenseIds
                    }).catch(() => {})
                  );
                }

                // Wait for all DB syncs to complete before rendering the page
                await Promise.allSettled(syncPromises);
                setLicensesSyncKey(k => k + 1);

                let contactInfo = {
                  Contact:{
                  contactid: String(res.Id),
                  firstname: res.FirstName,
                  middlename:res.MiddleName,
                  lastname:res.LastName,
                  jobtitle:res.Job_Title,
                  professionaldesignation:res.Professional_Designation,
                  acepid:acepid,
                  isABEMCertified: acepData.isABEMCertified !== undefined ? acepData.isABEMCertified : false,
                  acepMemberStatus: Number(acepData.acepMemberStatus) || 0,
                  sempaMemberStatus: Number(acepData.sempaMemberStatus) || 0,
                  IsNonPhysician: res.IsNonPhysician || false,
                  SelfAttestationUpdatedAt: res.SelfAttestationUpdatedAt || null,
                  NPI_Number: profileContact?.npi || res.NPI_Number || null,
                  Birthdate: profileContact?.birthday || res.Birthdate || null,
                  PARSConsentAccepted: res.PARSConsentAccepted ?? null,
                  DeadlineWarningLastShown: res.DeadlineWarningLastShown ?? null,
                  city: acepData.city || '',
                  state: acepData.stateProvince || '',
                  country: acepData.country || ''
                  }
                };
                setContactObject(contactInfo);
                setLoadPage(true);
              });
          }
          else{
            fetchingAcepIdRef.current = null;
            history.push("/");
          }
        })

        .catch(function (error:any) {
          console.log(error);
          fetchingAcepIdRef.current = null;
          if(error.response.status === 404){
            console.log("Contact not found, creating new contact")
            const requestOptions = {

              Id:userData?.id,
              // Prefix: userData?.Prefix,
              FirstName: userData?.firstName,
              // MiddleName: userData?.middleName,
              LastName: userData?.lastName,
              FullName: userData?.formalName,
              // Birthdate: req.body.Birthdate,
              Email: userData?.email2,
              // Job_Title: req.body.Job_Title,
              // Professional_Designation:req.body. Professional_Designation,
              Phone_Number: userData?.phone,
              Acep_ContactNumber: userData?.acepid,
              IsActive: 1

            }; 
  
            apiProfiles.url = "/cmecontacts";
            apiProfiles.post(requestOptions)
            .then((data:any) => {

                var rows=data?.cmecontact || [];
              if(rows){

                let contactInfo = {
                  Contact:{
                  contactid: rows.Id,
                  firstname: rows.FirstName,
                middlename:rows.MiddleName,
                lastname:rows.LastName,
                jobtitle:rows.Job_Title,
                professionaldesignation:rows.Professional_Designation,
                acepid:rows.Acep_ContactNumber,
                isABEMCertified: userData?.isABEMCertified !== undefined ? userData.isABEMCertified : false,
                acepMemberStatus: Number(userData?.acepMemberStatus) || 0,
                sempaMemberStatus: Number(userData?.sempaMemberStatus) || 0,
                IsNonPhysician: rows.IsNonPhysician || false,
                SelfAttestationUpdatedAt: rows.SelfAttestationUpdatedAt || null,
                NPI_Number: null,
                Birthdate: null,
                PARSConsentAccepted: null,
                DeadlineWarningLastShown: null,
                city: userData?.city || '',
                state: userData?.state || '',
                country: userData?.country || ''
                }
              };


          setContactObject(contactInfo);
          setLoadPage(true)

              }
              else{
                history.push("/");
              }
                
            })
            .catch(function (error:any) {
              history.push("/");
            })
          }
          
        });
      }else if(!acepid){
        // Only clear when there is genuinely nobody to load. This used to be a bare `else`,
        // which also fired on the run right after a successful load (shouldMakeAPICall is
        // false once loadPage is true and the ids match) and wiped the contact that had just
        // arrived — sending the effect straight back round to fetch it again.
        fetchingAcepIdRef.current = null;
        setContactObject({Contact: {...EMPTY_CONTACT}});
        setLoadPage(false)
      }
  
  }, [userData?.acepid, queryAcepId, isAdmin, contactObject.Contact.acepid, userData, location.pathname]);
  return (
    <div>
      <contactContext.Provider value={{contactObject,updateContactInfo,updatePARSConsent,updateDeadlineWarningLastShown,isLoading:!loadPage,licensesSyncKey}}>
        {props.children}
        <PromiseContextProvider/>
      </contactContext.Provider>
    </div>
  );
}
